import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Shield,
  Database,
  FileText,
  Brain,
  Network,
  Search,
  CheckCircle2,
  ArrowRight,
  Users,
  Lock,
  Activity,
  Package,
  Syringe,
  Heart,
  Clock,
  Globe,
  ChevronDown,
  Zap,
  DollarSign,
  TrendingUp,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import Breadcrumbs from '@/components/Breadcrumbs';
import SEO from '@/components/SEO';
import ROIResultsCapture from '@/components/ROIResultsCapture';

const Healthcare = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [monthlyInvoices, setMonthlyInvoices] = useState(1000);
  const [costPerInvoice, setCostPerInvoice] = useState(18);
  const [exceptionRate, setExceptionRate] = useState(35);
  const [staffRate, setStaffRate] = useState(25);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ROI Calculator Logic
  const currentProcessingCost = monthlyInvoices * costPerInvoice;
  const hoursSpentPerMonth = (monthlyInvoices * 0.72); // 0.72 hours per invoice average
  const exceptionsHandledManually = monthlyInvoices * (exceptionRate / 100);
  const exceptionHandlingCost = (exceptionsHandledManually * 15) / 60 * staffRate; // 15 min per exception

  const processingTimeReduction = 0.75;
  const newProcessingCost = currentProcessingCost * (1 - processingTimeReduction);
  const hoursSaved = hoursSpentPerMonth * processingTimeReduction;
  const exceptionsHandledByAI = exceptionsHandledManually * 0.9;
  const manualExceptionsRemaining = exceptionsHandledManually * 0.1;
  const exceptionResolutionSavings = (exceptionsHandledByAI * 15) / 60 * staffRate;

  const laborSavings = hoursSaved * staffRate;
  const totalSavings = laborSavings + exceptionResolutionSavings;
  const ezCloudCost = 3000;
  const netMonthlySavings = totalSavings - ezCloudCost;
  const annualSavings = netMonthlySavings * 12;
  const roi = (annualSavings / (ezCloudCost * 12)) * 100;
  const paybackMonths = (ezCloudCost / netMonthlySavings);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [] as Array<{
      '@type': 'Question';
      name: string;
      acceptedAnswer: { '@type': 'Answer'; text: string };
    }>,
  };

  const faqs = [
    {
      question: "Is EZ Cloud HIPAA compliant?",
      answer: "Yes. EZ Cloud is a HIPAA-compliant business associate. We sign a Business Associate Agreement (BAA) with all healthcare customers, maintain SOC 2 Type II certification, and follow all HIPAA security and privacy requirements."
    },
    {
      question: "Do you sign a Business Associate Agreement (BAA)?",
      answer: "Yes. All healthcare customers receive a signed BAA before processing any invoices. The BAA is included at no additional cost."
    },
    {
      question: "How is our data encrypted?",
      answer: "All data is encrypted: In transit with TLS 1.3 (256-bit), at rest with AES-256, database with column-level encryption, and backups are encrypted and geo-redundant. Encryption keys are managed via AWS KMS (hardware security modules)."
    },
    {
      question: "Do you integrate with Oracle E-Business Suite (EBS)?",
      answer: "Yes. We integrate with Oracle EBS through the standard AP Invoice Open Interface — no direct writes to AP base tables. Most healthcare organizations run Oracle EBS and we have deep experience with it. Implementation typically runs 8-12 weeks."
    },
    {
      question: "Do you integrate with Oracle Cloud ERP (Fusion)?",
      answer: "Yes. We integrate with Oracle Cloud ERP (Fusion) using FBDI for invoice load and Oracle Integration Cloud (OIC) REST for real-time validation. Cloud deployments often land toward the shorter end of the 8-12 week range since there's no VPN or firewall configuration needed."
    },
    {
      question: "What about Epic ERP?",
      answer: "Many healthcare organizations use Epic for clinical operations and Oracle for financials. We integrate with Oracle, which feeds Epic indirectly. This 'system of record' approach ensures data consistency across systems."
    },
    {
      question: "How does vendor credentialing work?",
      answer: "EZ Cloud tracks vendor credentials automatically: Upload vendor insurance certificates, W-9s, licenses; set expiration dates; receive email alerts 30/60/90 days before expiration; vendors can update credentials via self-service portal; audit reports show credential status for all vendors."
    },
    {
      question: "What is AI Exception Resolution?",
      answer: "When invoices have exceptions (missing PO, quantity mismatch, etc.), EZ Cloud's AI automatically emails the right person with a specific request. AI parses responses, updates invoices, and progresses workflow. Result: 90% of exceptions resolved without AP staff involvement."
    },
    {
      question: "How accurate is the data extraction?",
      answer: "EZ Cloud reaches 88-92% touchless processing on healthcare invoices, including complex line item tables (50-200 lines), handwritten notes and annotations, NDC codes and lot numbers, and multiple pages and formats. Our triple-layer AI combines structured data extraction, table extraction, and custom entity recognition for healthcare-specific fields, with anything uncertain routed to a human."
    },
    {
      question: "How long does implementation take?",
      answer: "Typical timeline is 8-12 weeks: discovery and Oracle access, integration setup, supplier-master and chart-of-accounts alignment, approval-workflow configuration, UAT, and a parallel run into go-live. Test invoices can be processed in a sandbox early in the project."
    }
  ];

  faqSchema.mainEntity = faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  }));

  return (
    <>
      <SEO
        title="HIPAA-Compliant AP Automation for Healthcare on Oracle Fusion | EZ Cloud"
        description="HIPAA-compliant AP automation for healthcare on Oracle Fusion. AI capture, vendor credentialing, SOC 2 + ISO 27001. Published on Oracle's reference architecture."
        canonical="https://ezcloud.ai/industries/healthcare"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Industries', url: 'https://ezcloud.ai/#industries' },
          { name: 'Healthcare', url: 'https://ezcloud.ai/industries/healthcare' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen">
        <Navigation />
        <Breadcrumbs />

        {/* Oracle Fusion solution cross-link */}
        <div className="bg-accent/10 border-b border-accent/20">
          <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-gray-700">
            <span className="font-medium">Running Oracle Fusion Cloud ERP?</span>
            <Link
              to="/solutions/healthcare-oracle-fusion"
              className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
            >
              See the Healthcare-on-Oracle-Fusion solution
              <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
            </Link>
          </div>
        </div>

        {/* Oracle WebCenter modernization cross-link */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-gray-700">
            <span className="font-medium">Running Oracle WebCenter AP today?</span>
            <Link
              to="/solutions/modernize-oracle-webcenter"
              className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
            >
              See the Oracle WebCenter AP modernization path
              <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
            </Link>
          </div>
        </div>

        {/* HERO SECTION */}
        <section className="bg-hero-gradient text-white min-h-[90vh] relative overflow-hidden flex items-center">
          {/* Background Elements */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
            <div className="absolute top-20 right-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
          </div>

          <div className="container mx-auto px-4 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* LEFT COLUMN - Content */}
              <div className="text-center lg:text-left space-y-8">
                {/* Badge */}
                <div className="flex justify-center lg:justify-start gap-3">
                  <Badge className="bg-accent/20 text-accent border-accent/30 px-4 py-2 text-sm">
                    <Shield className="w-4 h-4 mr-2" />
                    HIPAA Compliant
                  </Badge>
                  <Badge className="bg-accent/20 text-accent border-accent/30 px-4 py-2 text-sm">
                    <Lock className="w-4 h-4 mr-2" />
                    SOC 2 Type II
                  </Badge>
                </div>

                {/* Headline */}
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
                  Invoice Automation for
                  <span className="text-accent block">Healthcare Organizations</span>
                </h1>

                {/* Description - Two paragraphs, same size, different opacity */}
                <div className="space-y-4">
                  <p className="text-lg lg:text-xl text-white/90 leading-relaxed">
                    Healthcare organizations face unique accounts payable challenges: thousands of vendor
                    invoices, complex approval workflows, vendor credentialing requirements, and strict
                    HIPAA compliance mandates. Manual processing creates bottlenecks, increases costs,
                    and introduces compliance risks.
                  </p>

                  <p className="text-lg lg:text-xl text-white/80 leading-relaxed">
                    EZ Cloud automates your entire healthcare AP workflow with HIPAA-compliant infrastructure,
                    native Oracle Fusion integration, and AI-powered exception prevention—delivering 88–92%
                    touchless processing while maintaining the audit trails and security controls healthcare demands.
                  </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-6 py-4">
                  <div>
                    <div className="text-3xl font-bold text-accent">88–92%</div>
                    <div className="text-sm text-white/80">Touchless Processing</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-accent">Up to 90%</div>
                    <div className="text-sm text-white/80">Email Exceptions Prevented</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-accent">25 yrs</div>
                    <div className="text-sm text-white/80">Inside Oracle AP</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-accent">100%</div>
                    <div className="text-sm text-white/80">Oracle-Supported Posting</div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                  <Button
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                    onClick={() => openCalendlyDemo()}
                  >
                    Schedule Healthcare Demo
                  </Button>
                  <Button
                    size="lg"
                    variant="outline-on-dark"
                    className="transition-all duration-300 px-8 py-6 text-lg font-semibold"
                    asChild
                  >
                    <a href="#compliance">View Compliance Details</a>
                  </Button>
                </div>
              </div>

              {/* RIGHT COLUMN - Healthcare Visual */}
              <div className="hidden lg:block relative">
                <div className="relative w-full h-[600px]">
                  {/* Hospital Building */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-48 h-72 bg-gradient-to-t from-blue-600/30 to-blue-400/20 rounded-t-lg backdrop-blur-sm border-2 border-blue-400/30 relative overflow-hidden">
                      {/* Healthcare Cross on top */}
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                        <div className="w-12 h-12 bg-blue-400/40 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-blue-300/40">
                          <Heart className="w-6 h-6 text-white animate-pulse" />
                        </div>
                      </div>
                      {/* Building Windows - Pulsing like heartbeat */}
                      <div className="grid grid-cols-4 gap-2 p-4 mt-4">
                        {[...Array(32)].map((_, i) => (
                          <div
                            key={i}
                            className="w-full aspect-square bg-white/20 rounded-sm animate-pulse"
                            style={{ animationDelay: `${(i % 4) * 0.2}s`, animationDuration: '2s' }}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Floating Medical Supply Invoice - Top Left */}
                  <div className="absolute top-16 left-12 animate-float">
                    <div className="w-28 h-36 bg-white/90 rounded-lg shadow-2xl p-3 transform -rotate-12 border-l-4 border-green-500">
                      <Syringe className="w-6 h-6 text-green-600 mb-2" />
                      <div className="space-y-1">
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-3 bg-green-100 rounded w-1/2 mt-2"></div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Pharmaceutical Invoice - Top Right */}
                  <div className="absolute top-24 right-16 animate-float" style={{ animationDelay: '0.5s' }}>
                    <div className="w-28 h-36 bg-white/90 rounded-lg shadow-2xl p-3 transform rotate-6 border-l-4 border-blue-500">
                      <Package className="w-6 h-6 text-blue-600 mb-2" />
                      <div className="space-y-1">
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-3 bg-blue-100 rounded w-1/2 mt-2"></div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Equipment Invoice - Bottom Left */}
                  <div className="absolute bottom-24 left-20 animate-float" style={{ animationDelay: '1s' }}>
                    <div className="w-28 h-36 bg-white/90 rounded-lg shadow-2xl p-3 transform rotate-12 border-l-4 border-purple-500">
                      <Activity className="w-6 h-6 text-purple-600 mb-2" />
                      <div className="space-y-1">
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-3 bg-purple-100 rounded w-1/2 mt-2"></div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Services Invoice - Bottom Right */}
                  <div className="absolute bottom-20 right-20 animate-float" style={{ animationDelay: '1.5s' }}>
                    <div className="w-28 h-36 bg-white/90 rounded-lg shadow-2xl p-3 transform -rotate-6 border-l-4 border-orange-500">
                      <Users className="w-6 h-6 text-orange-600 mb-2" />
                      <div className="space-y-1">
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                        <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-3 bg-orange-100 rounded w-1/2 mt-2"></div>
                      </div>
                    </div>
                  </div>

                  {/* Central AI Processing Hub with Healthcare Theme */}
                  <div className="absolute top-1/2 left-1/4 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="relative w-36 h-36">
                      {/* Rotating outer ring */}
                      <div className="absolute inset-0 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
                      {/* Inner circle */}
                      <div className="absolute inset-4 bg-gradient-to-br from-blue-500/30 to-blue-600/30 backdrop-blur-md rounded-full flex flex-col items-center justify-center border-2 border-blue-400/40">
                        <Brain className="w-10 h-10 text-blue-300 animate-pulse mb-1" />
                        <div className="text-xs font-bold text-white">AI Hub</div>
                      </div>
                    </div>
                  </div>

                  {/* HIPAA Security Shields - Rotating around */}
                  <div className="absolute top-1/3 right-1/4 animate-float" style={{ animationDelay: '0.3s' }}>
                    <div className="w-16 h-16 bg-green-500/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-green-400/40">
                      <Shield className="w-8 h-8 text-green-300" />
                    </div>
                  </div>

                  <div className="absolute bottom-1/3 right-1/3 animate-float" style={{ animationDelay: '0.7s' }}>
                    <div className="w-14 h-14 bg-blue-500/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-blue-400/40">
                      <Lock className="w-7 h-7 text-blue-300" />
                    </div>
                  </div>

                  {/* Compliance Badges - Floating */}
                  <div className="absolute top-10 left-1/4 animate-float" style={{ animationDelay: '0.2s' }}>
                    <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full border border-white/20">
                      <span className="text-xs text-white font-semibold">HIPAA</span>
                    </div>
                  </div>

                  <div className="absolute bottom-16 left-1/3 animate-float" style={{ animationDelay: '0.9s' }}>
                    <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full border border-white/20">
                      <span className="text-xs text-white font-semibold">SOC 2</span>
                    </div>
                  </div>

                  <div className="absolute top-1/4 right-12 animate-float" style={{ animationDelay: '1.2s' }}>
                    <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full border border-white/20">
                      <span className="text-xs text-white font-semibold">Encrypted</span>
                    </div>
                  </div>

                  {/* Green Checkmarks for Processed Invoices */}
                  <div className="absolute top-20 left-1/2 animate-bounce" style={{ animationDelay: '0.5s' }}>
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  <div className="absolute bottom-28 right-1/4 animate-bounce" style={{ animationDelay: '1s' }}>
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges Section */}
        <section className="bg-white py-12 border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
              <div className="flex flex-col items-center">
                <Shield className="w-8 h-8 text-accent mb-2" />
                <div className="font-semibold text-sm">HIPAA Compliant</div>
              </div>
              <div className="flex flex-col items-center">
                <Lock className="w-8 h-8 text-accent mb-2" />
                <div className="font-semibold text-sm">SOC 2 Type II Certified</div>
              </div>
              <div className="flex flex-col items-center">
                <Database className="w-8 h-8 text-accent mb-2" />
                <div className="font-semibold text-sm">256-bit AES Encryption</div>
              </div>
              <div className="flex flex-col items-center">
                <CheckCircle2 className="w-8 h-8 text-accent mb-2" />
                <div className="font-semibold text-sm">Oracle Certified Partner</div>
              </div>
            </div>
          </div>
        </section>

        {/* US + AUSTRALIA PRESENCE */}
        <section className="py-16 bg-gray-50 border-b">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Health systems in the US and Australia
            </p>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Run on Oracle Fusion or EBS, on either side of the Pacific
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              EZ Cloud runs AP automation for healthcare organisations in North America
              and Australia. We deploy into your chosen Oracle Cloud Infrastructure
              region — so an Australian health service keeps its data in-country,
              under local data-residency rules, with the same integration we run
              for US health systems.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 text-left">
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <Globe className="w-6 h-6 text-accent mb-2" aria-hidden />
                <div className="font-semibold text-gray-900 mb-1">In-region data residency</div>
                <div className="text-sm text-gray-600">Deploy in AU, US, EU, or US-gov cloud regions. Data stays where your compliance team needs it.</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <Clock className="w-6 h-6 text-accent mb-2" aria-hidden />
                <div className="font-semibold text-gray-900 mb-1">Local-hours support</div>
                <div className="text-sm text-gray-600">Implementation and support aligned to your business hours, AEST or US time zones.</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <Shield className="w-6 h-6 text-accent mb-2" aria-hidden />
                <div className="font-semibold text-gray-900 mb-1">Local compliance</div>
                <div className="text-sm text-gray-600">HIPAA-compliant in the US; privacy and health-data rules respected for Australian deployments.</div>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM STATEMENT SECTION */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4">Healthcare AP Processing is Broken</h2>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  Manual invoice processing costs healthcare organizations time, money, and creates compliance risks
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Problem 1 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Clock className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle className="text-xl">Invoice Volume Overwhelms Staff</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Healthcare organizations process thousands of invoices monthly: medical and surgical supplies, pharmaceuticals and medications, equipment purchases and maintenance, service providers and consultants, and facilities and utilities.
                    </p>
                    <p className="text-gray-600 font-semibold">
                      Your AP team spends 60-80 hours per 1,000 invoices manually entering data, chasing approvals, and resolving exceptions.
                    </p>
                    <p className="text-red-600 font-bold mt-2">
                      Time wasted: $18,000-24,000 per month for a mid-size hospital
                    </p>
                  </CardContent>
                </Card>

                {/* Problem 2 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Shield className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle className="text-xl">Compliance Risks Are Everywhere</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Manual AP processes create audit and compliance vulnerabilities:
                    </p>
                    <ul className="text-gray-600 space-y-2 mb-4">
                      <li className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        Missing or incomplete audit trails
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        Vendor files lack current credentials
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        Paper approvals create gaps in documentation
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        No systematic tracking of HIPAA-related vendor access
                      </li>
                    </ul>
                    <p className="text-red-600 font-bold">
                      Result: Audit findings, regulatory fines, and compliance headaches
                    </p>
                  </CardContent>
                </Card>

                {/* Problem 3 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle className="text-xl">Vendor Management is Complex</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Healthcare vendors require special tracking: W-9 forms for tax reporting, insurance certificates (General Liability, Professional Liability), state/federal licenses and certifications, expiration date monitoring, and compliance documentation.
                    </p>
                    <p className="text-gray-600 font-semibold">
                      Your team maintains spreadsheets, emails, and file cabinets trying to track hundreds of vendors. Documents expire without notice.
                    </p>
                  </CardContent>
                </Card>

                {/* Problem 4 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Search className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle className="text-xl">Exception Handling Bogs Down AP</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      30-40% of healthcare invoices have exceptions: missing purchase orders, quantity/price mismatches, wrong department or cost center assignments, invoices for non-contracted items, and incomplete receiving documentation.
                    </p>
                    <p className="text-red-600 font-bold">
                      Staff sends 50-100 emails daily chasing suppliers, department heads, and purchasing teams. Invoices sit in queues for days or weeks.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* SOLUTION OVERVIEW SECTION */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4">How EZ Cloud Solves Healthcare AP Automation</h2>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  The only AP automation platform purpose-built for healthcare organizations
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {/* Step 1 */}
                <Card className="border-2 border-primary/20">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-sm font-semibold text-primary mb-2">STEP 1</div>
                    <CardTitle className="text-xl">Intelligent Capture</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Invoices arrive via email forwarding, supplier portal uploads, EDI feeds, or scanned documents.
                    </p>
                    <p className="text-gray-700 font-semibold mb-2">AI extracts ALL fields:</p>
                    <ul className="text-gray-600 text-sm space-y-1 mb-4">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Vendor details
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Invoice number, date, amount
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Line items with NDC codes
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Department/cost center
                      </li>
                    </ul>
                    <div className="bg-green-50 border-l-4 border-green-500 p-3">
                      <p className="text-sm font-bold text-green-700">88-92% touchless processing</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Step 2 */}
                <Card className="border-2 border-primary/20">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Network className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-sm font-semibold text-primary mb-2">STEP 2</div>
                    <CardTitle className="text-xl">Smart Validation & Routing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 font-semibold mb-2">Automated validation:</p>
                    <ul className="text-gray-600 text-sm space-y-1 mb-4">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Vendor exists in Oracle
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Vendor credentials current
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        PO matches (if required)
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        GL codes valid
                      </li>
                    </ul>
                    <p className="text-gray-600 text-sm mb-2">
                      Intelligent routing to department heads, budget owners, or grant managers. Approvers receive email notification with one-click approval link.
                    </p>
                  </CardContent>
                </Card>

                {/* Step 3 */}
                <Card className="border-2 border-accent/20 relative">
                  <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">Unique</Badge>
                  <CardHeader>
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                      <Brain className="w-6 h-6 text-accent" />
                    </div>
                    <div className="text-sm font-semibold text-accent mb-2">STEP 3</div>
                    <CardTitle className="text-xl">AI Exception Resolution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4 text-sm">
                      When exceptions occur, AI automatically:
                    </p>
                    <ol className="text-gray-600 text-sm space-y-2 mb-4 list-decimal list-inside">
                      <li>Detects the specific issue</li>
                      <li>Emails the right person with specific request</li>
                      <li>Parses email responses and updates invoice</li>
                      <li>Progresses invoice through workflow automatically</li>
                    </ol>
                    <div className="bg-accent/10 border-l-4 border-accent p-3">
                      <p className="text-sm font-bold text-accent">
                        Result: 90% of exceptions resolved without AP staff involvement
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* KEY FEATURES SECTION */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4">Healthcare-Specific Features</h2>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  Purpose-built for the unique requirements of healthcare AP
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Feature 1 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">HIPAA-Compliant Infrastructure</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Built for healthcare security and compliance:
                    </p>
                    <ul className="text-gray-600 text-sm space-y-2">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        SOC 2 Type II Certified
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        HIPAA Compliant Infrastructure
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        256-bit AES Encryption
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Complete audit logs
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Business Associate Agreement (BAA) included
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Feature 2 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Database className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Native Oracle ERP Integration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Deep integration with Oracle (EBS and Cloud ERP):
                    </p>
                    <ul className="text-gray-600 text-sm space-y-2 mb-4">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Real-time API integration, prebuilt by EZ Cloud
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Bi-directional data sync
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Automatic invoice validation
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Duplicate detection via API
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Implementation: 8-12 weeks
                      </li>
                    </ul>
                    <Link
                      to="/integrations/oracle"
                      className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary/80"
                    >
                      Explore the Oracle integration
                      <ArrowRight className="ml-1 w-4 h-4" aria-hidden />
                    </Link>
                  </CardContent>
                </Card>

                {/* Feature 3 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Vendor Credentialing Management</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Track vendor credentials automatically:
                    </p>
                    <ul className="text-gray-600 text-sm space-y-2">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        W-9 Forms & Insurance certificates
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        State/Federal Licenses
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Expiration date monitoring
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        30/60/90 day email alerts
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Vendor self-service portal
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Feature 4 */}
                <Card className="border-2 border-accent/20 hover:border-accent transition-colors relative">
                  <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">Unique</Badge>
                  <CardHeader>
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                      <Brain className="w-6 h-6 text-accent" />
                    </div>
                    <CardTitle className="text-xl">AI Exception Resolution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      AI handles invoice exceptions FOR you:
                    </p>
                    <ul className="text-gray-600 text-sm space-y-2 mb-4">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-accent mr-2 mt-0.5 flex-shrink-0" />
                        Detects missing POs, mismatches, issues
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-accent mr-2 mt-0.5 flex-shrink-0" />
                        Emails right person automatically
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-accent mr-2 mt-0.5 flex-shrink-0" />
                        Parses responses, updates invoices
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-accent mr-2 mt-0.5 flex-shrink-0" />
                        90% resolution without AP staff
                      </li>
                    </ul>
                    <p className="text-sm font-semibold text-accent mb-3">
                      No other AP automation platform does this.
                    </p>
                    <Link
                      to="/technology/ai-email-prevention"
                      className="inline-flex items-center text-sm font-semibold text-accent hover:text-accent/80"
                    >
                      How AI Email Prevention works
                      <ArrowRight className="ml-1 w-4 h-4" aria-hidden />
                    </Link>
                  </CardContent>
                </Card>

                {/* Feature 5 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Network className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Department & Grant-Based Routing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Complex approval workflows made simple:
                    </p>
                    <ul className="text-gray-600 text-sm space-y-2">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Route by department/cost center
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Route by dollar amount
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Route by grant/fund
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        One-click approval for approvers
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Mobile-friendly approval workflow
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Feature 6 */}
                <Card className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Search className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Complete Audit Trail</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Audit-ready documentation automatically:
                    </p>
                    <ul className="text-gray-600 text-sm space-y-2">
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Complete activity history
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Who approved with timestamps
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        Email communications saved
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        7-year retention
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        One-click export for audits
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* HEALTHCARE USE CASES SECTION */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4">Healthcare Use Cases</h2>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  Real-world scenarios where EZ Cloud transforms healthcare AP
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Use Case 1 */}
                <Card className="border-2 border-green-200 hover:border-green-400 transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Syringe className="w-6 h-6 text-green-600" />
                    </div>
                    <CardTitle className="text-xl">Medical Supply Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-3">SCENARIO</p>
                    <p className="text-gray-600 mb-4">
                      Hospital processes 500+ medical supply invoices monthly from a long tail of national and regional distributors.
                    </p>
                    <p className="text-sm font-semibold text-gray-700 mb-2">CHALLENGES:</p>
                    <ul className="text-gray-600 text-sm space-y-1 mb-4">
                      <li>• High volume, low dollar amounts ($100-5,000)</li>
                      <li>• Detailed line items (50-200 items per invoice)</li>
                      <li>• NDC codes, lot numbers, expiration dates</li>
                      <li>• 3-way matching (PO, receipt, invoice)</li>
                    </ul>
                    <div className="bg-green-50 border-l-4 border-green-500 p-3">
                      <p className="text-sm font-bold text-green-700">Processing time: 15 min → 2 min (87% reduction)</p>
                      <p className="text-sm text-green-600">Cost per invoice: $18 → $3 (83% savings)</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Use Case 2 */}
                <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">Pharmaceutical Purchases</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-3">SCENARIO</p>
                    <p className="text-gray-600 mb-4">
                      Pharmacy department orders $2M monthly in pharmaceuticals from wholesalers and specialty distributors.
                    </p>
                    <p className="text-sm font-semibold text-gray-700 mb-2">CHALLENGES:</p>
                    <ul className="text-gray-600 text-sm space-y-1 mb-4">
                      <li>• High-value invoices ($10K-100K)</li>
                      <li>• Strict regulatory requirements (DEA tracking)</li>
                      <li>• NDC code accuracy critical</li>
                      <li>• Controlled substance documentation</li>
                    </ul>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3">
                      <p className="text-sm font-bold text-blue-700">Approval speed: 5 days → 1 day (80% faster)</p>
                      <p className="text-sm text-blue-600">Regulatory compliance: 100% documentation</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Use Case 3 */}
                <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Activity className="w-6 h-6 text-purple-600" />
                    </div>
                    <CardTitle className="text-xl">Equipment Maintenance & Repairs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-3">SCENARIO</p>
                    <p className="text-gray-600 mb-4">
                      Biomedical engineering manages maintenance for 1,000+ pieces of medical equipment (MRI, CT scanners, ventilators, monitors).
                    </p>
                    <p className="text-sm font-semibold text-gray-700 mb-2">CHALLENGES:</p>
                    <ul className="text-gray-600 text-sm space-y-1 mb-4">
                      <li>• Service invoices often lack PO numbers</li>
                      <li>• Emergency repairs need fast approval</li>
                      <li>• Equipment IDs must match asset tracking</li>
                      <li>• Warranty work vs billable repairs</li>
                    </ul>
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-3">
                      <p className="text-sm font-bold text-purple-700">Emergency payment: 7 days → 1 day</p>
                      <p className="text-sm text-purple-600">PO exceptions: 40% → 5% (AI resolves most)</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Use Case 4 */}
                <Card className="border-2 border-orange-200 hover:border-orange-400 transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-orange-600" />
                    </div>
                    <CardTitle className="text-xl">Service Provider Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-3">SCENARIO</p>
                    <p className="text-gray-600 mb-4">
                      Hospital contracts with physicians, consultants, and service providers for various services (locum tenens, IT, consulting).
                    </p>
                    <p className="text-sm font-semibold text-gray-700 mb-2">CHALLENGES:</p>
                    <ul className="text-gray-600 text-sm space-y-1 mb-4">
                      <li>• Professional services require current liability insurance</li>
                      <li>• Independent contractors need W-9 on file</li>
                      <li>• Services span multiple departments</li>
                      <li>• No physical goods (no receipt confirmation)</li>
                    </ul>
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-3">
                      <p className="text-sm font-bold text-orange-700">Processing time: 45 min → 5 min</p>
                      <p className="text-sm text-orange-600">Compliance: 100% current credentials</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* COMPLIANCE & SECURITY SECTION */}
        <section id="compliance" className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4">Enterprise-Grade Security for Healthcare</h2>
                <p className="text-lg lg:text-xl text-white/80 max-w-3xl mx-auto">
                  HIPAA-compliant infrastructure with the same security standards as electronic health records
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* HIPAA Compliance */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Shield className="w-8 h-8 text-accent mr-3" />
                    <h3 className="text-2xl font-bold">HIPAA COMPLIANCE</h3>
                  </div>
                  <p className="text-white/90 mb-4">
                    EZ Cloud meets all HIPAA requirements for business associates:
                  </p>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-white mb-1">✓ Administrative Safeguards</p>
                      <p className="text-sm text-white/80">Security Management, Workforce Security, Access Management</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1">✓ Physical Safeguards</p>
                      <p className="text-sm text-white/80">Facility Access Controls, Workstation Security, Device Controls</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1">✓ Technical Safeguards</p>
                      <p className="text-sm text-white/80">Access Controls, Audit Controls, Integrity Controls, Transmission Security</p>
                    </div>
                  </div>
                  <div className="mt-4 bg-accent/20 border-l-4 border-accent p-3 rounded">
                    <p className="text-sm font-semibold">Business Associate Agreement (BAA) provided to all healthcare customers</p>
                  </div>
                </div>

                {/* SOC 2 */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Lock className="w-8 h-8 text-accent mr-3" />
                    <h3 className="text-2xl font-bold">SOC 2 TYPE II CERTIFIED</h3>
                  </div>
                  <p className="text-white/90 mb-4">
                    Annual third-party audit confirms our security controls:
                  </p>
                  <ul className="space-y-2 text-white/90">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Security:</strong> Access controls, encryption, monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Availability:</strong> 99.9% uptime, redundancy, backup</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Processing Integrity:</strong> Data accuracy, completeness</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Confidentiality:</strong> Data protection, need-to-know access</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Privacy:</strong> PII handling, data retention</span>
                    </li>
                  </ul>
                </div>

                {/* Encryption */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Database className="w-8 h-8 text-accent mr-3" />
                    <h3 className="text-2xl font-bold">ENCRYPTION STANDARDS</h3>
                  </div>
                  <p className="text-white/90 mb-4">
                    All data protected with industry-leading encryption:
                  </p>
                  <ul className="space-y-2 text-white/90">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Data in Transit:</strong> TLS 1.3 (256-bit encryption)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Data at Rest:</strong> AES-256 encryption</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Database:</strong> Column-level encryption</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Backups:</strong> Encrypted and geo-redundant</span>
                    </li>
                  </ul>
                </div>

                {/* Access Controls */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Users className="w-8 h-8 text-accent mr-3" />
                    <h3 className="text-2xl font-bold">ACCESS CONTROLS</h3>
                  </div>
                  <ul className="space-y-2 text-white/90">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Role-Based Access Control (RBAC)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Multi-Factor Authentication (MFA) required</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Single Sign-On (SSO) via SAML 2.0</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>IP Whitelisting available</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Session timeout after inactivity</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Complete audit logging (7-year retention)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ROI CALCULATOR SECTION */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4">Calculate Your Savings</h2>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  See how much your healthcare organization could save with EZ Cloud
                </p>
              </div>

              <Card className="border-2 border-primary/20 shadow-lg">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    {/* Input Controls */}
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Monthly Invoice Volume</label>
                        <input
                          type="range"
                          min="100"
                          max="5000"
                          step="100"
                          value={monthlyInvoices}
                          onChange={(e) => setMonthlyInvoices(Number(e.target.value))}
                          className="w-full"
                        />
                        <div className="text-right text-2xl font-bold text-primary">{monthlyInvoices.toLocaleString()}</div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2">Current Cost per Invoice</label>
                        <input
                          type="range"
                          min="10"
                          max="30"
                          step="1"
                          value={costPerInvoice}
                          onChange={(e) => setCostPerInvoice(Number(e.target.value))}
                          className="w-full"
                        />
                        <div className="text-right text-2xl font-bold text-primary">${costPerInvoice}</div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2">Exception Rate (%)</label>
                        <input
                          type="range"
                          min="10"
                          max="50"
                          step="5"
                          value={exceptionRate}
                          onChange={(e) => setExceptionRate(Number(e.target.value))}
                          className="w-full"
                        />
                        <div className="text-right text-2xl font-bold text-primary">{exceptionRate}%</div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2">Staff Hourly Rate</label>
                        <input
                          type="range"
                          min="15"
                          max="50"
                          step="5"
                          value={staffRate}
                          onChange={(e) => setStaffRate(Number(e.target.value))}
                          className="w-full"
                        />
                        <div className="text-right text-2xl font-bold text-primary">${staffRate}/hr</div>
                      </div>
                    </div>

                    {/* Results */}
                    <div className="space-y-6">
                      <div className="bg-gradient-to-br from-primary to-primary/80 text-white rounded-lg p-6">
                        <div className="text-sm font-semibold mb-2">NET MONTHLY SAVINGS</div>
                        <div className="text-4xl font-bold mb-1">${Math.round(netMonthlySavings).toLocaleString()}</div>
                        <div className="text-sm opacity-90">per month</div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                          <div className="text-sm text-gray-600 mb-1">Annual Savings</div>
                          <div className="text-2xl font-bold text-green-600">${Math.round(annualSavings).toLocaleString()}</div>
                        </div>

                        <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                          <div className="text-sm text-gray-600 mb-1">ROI</div>
                          <div className="text-2xl font-bold text-blue-600">{Math.round(roi)}%</div>
                        </div>

                        <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                          <div className="text-sm text-gray-600 mb-1">Payback Period</div>
                          <div className="text-2xl font-bold text-purple-600">{paybackMonths.toFixed(1)} mo</div>
                        </div>

                        <div className="bg-white rounded-lg p-4 border-2 border-orange-200">
                          <div className="text-sm text-gray-600 mb-1">Time Saved</div>
                          <div className="text-2xl font-bold text-orange-600">{Math.round(hoursSaved)} hrs</div>
                        </div>
                      </div>

                      <div className="bg-accent/10 border-l-4 border-accent p-4 rounded">
                        <p className="text-sm font-semibold text-accent">
                          ✓ 88–92% Touchless Processing<br />
                          ✓ Up to 90% Email Exceptions Resolved Upstream<br />
                          ✓ Full Audit Trail Compliance
                        </p>
                      </div>
                    </div>
                  </div>

                  <ROIResultsCapture
                    industry="Healthcare"
                    annualSavings={annualSavings}
                    inputs={{
                      'Monthly invoices': monthlyInvoices.toLocaleString(),
                      'Cost per invoice': `$${costPerInvoice}`,
                      'Exception rate': `${exceptionRate}%`,
                      'Staff hourly rate': `$${staffRate}`,
                    }}
                    buttonLabel="Email My Healthcare ROI Report"
                  />
                  <div className="text-center mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-2 border-accent text-accent hover:bg-accent/10"
                      onClick={() => openCalendlyDemo()}
                    >
                      Or book a demo directly
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      <Link to="/resources/roi-calculator">
                        Full ROI calculator
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* RELATED PAGES SECTION */}
        <section className="py-16 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                  Related EZ Cloud resources for healthcare AP
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  How healthcare AP automation connects to your ERP, related industries, and customer stories.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link
                  to="/integrations/oracle"
                  className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
                >
                  <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                    Oracle EBS & Fusion
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </div>
                  <p className="text-sm text-gray-600">
                    Native AP integration with Oracle E-Business Suite and Oracle Cloud ERP — the dominant ERP in healthcare finance.
                  </p>
                </Link>

                <Link
                  to="/integrations/sap"
                  className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
                >
                  <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                    SAP ECC & S/4HANA
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </div>
                  <p className="text-sm text-gray-600">
                    MIRO replacement for healthcare systems running on SAP — keep SAP, ditch the manual MIRO entry.
                  </p>
                </Link>

                <Link
                  to="/integrations/dynamics"
                  className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
                >
                  <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                    Microsoft Dynamics 365
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </div>
                  <p className="text-sm text-gray-600">
                    F&O and Business Central integration for healthcare organizations on Dynamics 365.
                  </p>
                </Link>

                <Link
                  to="/customers"
                  className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
                >
                  <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                    Customer stories
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </div>
                  <p className="text-sm text-gray-600">
                    Real outcomes from EZ Cloud customers — processing time reductions, error rates, audit-readiness wins.
                  </p>
                </Link>

                <Link
                  to="/technology/native-erp"
                  className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
                >
                  <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                    How native ERP integration works
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </div>
                  <p className="text-sm text-gray-600">
                    Real-time APIs, zero-downtime deployment, SOC 2 compliance — the architecture that makes healthcare ERP integration safe.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4">Frequently Asked Questions</h2>
                <p className="text-lg lg:text-xl text-gray-600">
                  Everything you need to know about EZ Cloud for healthcare
                </p>
              </div>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index} className="border-2 hover:border-primary transition-colors">
                    <CardContent className="p-0">
                      <button
                        className="w-full text-left p-6 flex items-start justify-between"
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      >
                        <span className="font-semibold text-lg pr-4">{faq.question}</span>
                        <ChevronDown
                          className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${
                            openFaq === index ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaq === index && (
                        <div className="px-6 pb-6 text-gray-600">
                          {faq.answer}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                Ready to Transform Your Healthcare AP Process?
              </h2>
              <p className="text-lg lg:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Join healthcare organizations nationwide who trust EZ Cloud for HIPAA-compliant invoice automation
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                  onClick={() => openCalendlyDemo()}
                >
                  Schedule Healthcare Demo
                </Button>
                <Button
                  size="lg"
                  variant="outline-on-dark"
                  className="transition-all duration-300 px-8 py-6 text-lg font-semibold"
                  asChild
                >
                  <a href="#compliance">Download Compliance Overview</a>
                </Button>
              </div>

              <p className="text-white/70">
                HIPAA compliant • SOC 2 Type II certified • Native Oracle integration
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-12 border-t border-white/20">
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">88–92%</div>
                  <div className="text-sm text-white/80">Touchless Processing</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">Up to 90%</div>
                  <div className="text-sm text-white/80">Email Exceptions Prevented</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">25 yrs</div>
                  <div className="text-sm text-white/80">Inside Oracle AP</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">100%</div>
                  <div className="text-sm text-white/80">Oracle-Supported Posting</div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Healthcare;
